import {
  BudgetLineItem,
  BudgetTransferLine,
  CashflowAnnualLine,
  CashflowMonthlyLine,
  ContingencyPoint,
  ContingencyTableRow,
  PcgReport,
  PcgReportMeta,
  PcgReportStatus,
  PcgReportStatusTone,
  PcgReportSummary,
  ProvisionalSumRegisterLineItem,
  VariationLineItem,
  buildDefaultReportBuilderSections,
} from '../models'

const todayIso = () => new Date().toISOString().slice(0, 10)

export const mockPcgReportMeta: PcgReportMeta = {
  id: 'PCG-11',
  projectId: 'PROJECT-001',
  projectName: 'Project 1',
  mode: 'edit',
  status: 'draft',
  statusLabel: 'Draft',
  documentStatus: 'draft',
  reportNamingConvention: 'PCG',
  referenceSequenceNumber: '001',
  lastUpdated: todayIso(),
}

export const mockPcgReportSummaries: PcgReportSummary[] = [
  { id: 'PCG-11', reportNumber: 'NOV 2025-PCG 11', dateOfIssue: '2025-11-03', status: 'draft', statusLabel: 'Draft', statusTone: 'muted', createdAt: '2025-11-03', updatedAt: '2025-11-03' },
  { id: 'PCG-10', reportNumber: 'OCT 2025-PCG 10', dateOfIssue: '2025-10-03', status: 'final', statusLabel: 'Final', statusTone: 'info', createdAt: '2025-10-03', updatedAt: '2025-10-03' },
  { id: 'PCG-09', reportNumber: 'SEP 2025-PCG 9', dateOfIssue: '2025-09-04', status: 'final', statusLabel: 'Final', statusTone: 'info', createdAt: '2025-09-04', updatedAt: '2025-09-04' },
]

const currency = (value: number) => Number(value.toFixed(2))

const mockStatusAmounts = (overrides: Partial<Record<string, number>> = {}) => ({
  'rejected-withdrawn': 0,
  estimated: 0,
  submitted: 0,
  'dcwc-recommended': 0,
  'pcg-superintendent-endorsed': 0,
  ...overrides,
})

const budgetLines: BudgetLineItem[] = [
  {
    id: '1',
    itemNumber: '1.00',
    title: 'MAIN WORKS',
    section: 'header',
    children: [
      {
        id: '1-1',
        parentId: '1',
        itemNumber: '1.01',
        title: 'Demolition',
        initialBudget: 0,
        budgetTransfers: 0,
        adjustedBudget: 0,
        anticipated: 0,
        endCost: 0,
        variance: 0,
        committed: 0,
        nonCommitted: 0,
        expenditure: 0,
        costToComplete: 0,
      },
      {
        id: '1-2',
        parentId: '1',
        itemNumber: '1.02',
        title: 'Building Construction',
        initialBudget: 0,
        budgetTransfers: 0,
        adjustedBudget: 0,
        anticipated: 0,
        endCost: 0,
        variance: 0,
        committed: 0,
        nonCommitted: 0,
        expenditure: 0,
        costToComplete: 0,
      },
    ],
  },
  {
    id: '2',
    itemNumber: '2.00',
    title: 'VARIATIONS',
    section: 'header',
    children: [
      {
        id: '2-1',
        parentId: '2',
        itemNumber: '2.01',
        title: 'Rejected/Withdrawn',
        initialBudget: 0,
        budgetTransfers: 0,
        adjustedBudget: 0,
        anticipated: 0,
        endCost: 0,
        variance: 0,
        committed: 0,
        nonCommitted: 0,
        expenditure: 0,
        costToComplete: 0,
      },
      {
        id: '2-2',
        parentId: '2',
        itemNumber: '2.02',
        title: 'Estimated',
        initialBudget: 0,
        budgetTransfers: 0,
        adjustedBudget: 0,
        anticipated: 0,
        endCost: 0,
        variance: 0,
        committed: 0,
        nonCommitted: 0,
        expenditure: 0,
        costToComplete: 0,
      },
      {
        id: '2-3',
        parentId: '2',
        itemNumber: '2.03',
        title: 'Submitted',
        initialBudget: 0,
        budgetTransfers: 0,
        adjustedBudget: 0,
        anticipated: 0,
        endCost: 0,
        variance: 0,
        committed: 0,
        nonCommitted: 0,
        expenditure: 0,
        costToComplete: 0,
      },
    ],
  },
  {
    id: '3',
    itemNumber: '3.00',
    title: 'CONTINGENCY',
    section: 'header',
    children: [
      {
        id: '3-1',
        parentId: '3',
        itemNumber: '3.01',
        title: 'Construction Contingency',
        initialBudget: 0,
        budgetTransfers: 0,
        adjustedBudget: 0,
        anticipated: 0,
        endCost: 0,
        variance: 0,
        committed: 0,
        nonCommitted: 0,
        expenditure: 0,
        costToComplete: 0,
      },
      {
        id: '3-2',
        parentId: '3',
        itemNumber: '3.02',
        title: 'Prolongation Contingency',
        initialBudget: 0,
        budgetTransfers: 0,
        adjustedBudget: 0,
        anticipated: 0,
        endCost: 0,
        variance: 0,
        committed: 0,
        nonCommitted: 0,
        expenditure: 0,
        costToComplete: 0,
      },
    ],
  },
  {
    id: '4',
    itemNumber: '4.00',
    title: 'OTHER COSTS',
    section: 'header',
    children: [
      {
        id: '4-1',
        parentId: '4',
        itemNumber: '4.01',
        title: 'Consultant Fees & Traditional Owner Engagement',
        initialBudget: 0,
        budgetTransfers: 0,
        adjustedBudget: 0,
        anticipated: 0,
        endCost: 0,
        variance: 0,
        committed: 0,
        nonCommitted: 0,
        expenditure: 0,
        costToComplete: 0,
      },
    ],
  },
]

const variationLines: VariationLineItem[] = [
  {
    id: 'v-1',
    variationNumber: 'VO-001',
    description: 'Additional structural reinforcement for seismic upgrade',
    statusAmounts: mockStatusAmounts({ submitted: currency(45000) }),
    payment: currency(45000),
    maximumPaymentValue: currency(45000),
    fundingSource: 'Equipment Financing',
    statusLabel: 'PCG Superintendent Endorsed',
    statusTone: 'info',
    cause: 'Structural upgrade',
    notes: 'Sample Notes',
  },
  {
    id: 'v-2',
    variationNumber: 'VO-002',
    description: 'Additional asbestos removal works',
    statusAmounts: mockStatusAmounts({ submitted: currency(40900) }),
    payment: currency(40900),
    maximumPaymentValue: currency(40900),
    fundingSource: 'Grants',
    statusLabel: 'PCG Superintendent Endorsed',
    statusTone: 'info',
    cause: 'Compliance',
    notes: 'Sample Notes',
  },
  {
    id: 'v-total',
    variationNumber: '',
    description: 'TOTAL END COST (Excluding GST)',
    statusAmounts: mockStatusAmounts({ submitted: currency(85900) }),
    payment: currency(85900),
    maximumPaymentValue: currency(85900),
    fundingSource: '',
    statusLabel: '',
    statusTone: 'muted',
    isTotalRow: true,
  },
]

const provisionalSumRegisterLines: ProvisionalSumRegisterLineItem[] = [
  {
    id: 'ps-parent-1',
    variationNumber: '',
    description: 'Provisional Sum',
    statusAmounts: mockStatusAmounts({ submitted: currency(45000) }),
    payment: currency(45000),
    maximumPaymentValue: currency(45000),
    approvedInitialValue: currency(45000),
    approvedCommitment: currency(45000),
    uncommitted: 0,
    expenditure: currency(90000),
    remainingProvisionalSum: currency(-45000),
    comments: 'Sample note',
    children: [
      {
        id: 'ps-1',
        parentId: 'ps-parent-1',
        variationNumber: 'VO-012',
        description: 'Arena fencing',
        statusAmounts: mockStatusAmounts({ submitted: currency(45000) }),
        payment: currency(45000),
        maximumPaymentValue: currency(45000),
        approvedInitialValue: currency(45000),
        approvedCommitment: null,
        uncommitted: null,
        expenditure: null,
        remainingProvisionalSum: null,
        comments: '',
      },
    ],
  },
  {
    id: 'ps-total',
    variationNumber: '',
    description: 'TOTAL',
    statusAmounts: mockStatusAmounts({ submitted: currency(45000) }),
    payment: currency(45000),
    maximumPaymentValue: currency(45000),
    approvedInitialValue: currency(45000),
    approvedCommitment: 0,
    uncommitted: 0,
    expenditure: 0,
    remainingProvisionalSum: 0,
    comments: '',
    isTotalRow: true,
  },
]

const budgetTransfers: BudgetTransferLine[] = [
  {
    id: 'bt-1',
    date: '',
    description: '',
    amount: 0,
    from: '',
    to: '',
  },
]

const months = [
  'SEP-25',
  'OCT-25',
  'NOV-25',
  'DEC-25',
  'JAN-26',
  'FEB-26',
  'MAR-26',
  'APR-26',
  'MAY-26',
  'JUN-26',
  'JUL-26',
  'AUG-26',
  'SEP-26',
]

const monthlyActuals = [230000, 245000, 255000, 190000, 175000, 165000, 150000, 145000, 135000, 125000, 115000, 110000, 105000]
const cashflowMonthlyBase: CashflowMonthlyLine[] = []
let forecastRunningTotal = 0
let actualRunningTotal = 0

months.forEach((month, idx) => {
  const forecastMonthly = 250000
  const actualMonthly = monthlyActuals[idx] ?? 0
  forecastRunningTotal += forecastMonthly
  actualRunningTotal += actualMonthly

  cashflowMonthlyBase.push({
    id: `cfm-${month}`,
    month,
    forecastMonthly,
    forecastCumulative: forecastRunningTotal,
    actualMonthly,
    actualCumulative: actualRunningTotal,
  })
})

const cashflowMonthly: CashflowMonthlyLine[] = [...cashflowMonthlyBase]
const constructionCashflowMonthly: CashflowMonthlyLine[] = cashflowMonthlyBase.map((row) => ({
  ...row,
  id: `cc-${row.id}`,
}))

const sumField = <T, K extends keyof T>(rows: T[], field: K) =>
  rows.reduce((sum, row) => sum + ((row[field] as unknown as number | null | undefined) ?? 0), 0)

const cashflowAnnual: CashflowAnnualLine[] = [
  {
    id: 'cfa-2026',
    year: '2025-2026',
    forecastAnnual: sumField(cashflowMonthlyBase.slice(0, 10), 'forecastMonthly'),
    forecastCumulative: sumField(cashflowMonthlyBase.slice(0, 10), 'forecastMonthly'),
    actualAnnual: sumField(cashflowMonthlyBase.slice(0, 10), 'actualMonthly'),
    actualCumulative: sumField(cashflowMonthlyBase.slice(0, 10), 'actualMonthly'),
  },
  {
    id: 'cfa-2027',
    year: '2026-2027',
    forecastAnnual: sumField(cashflowMonthlyBase.slice(10), 'forecastMonthly'),
    forecastCumulative: sumField(cashflowMonthlyBase, 'forecastMonthly'),
    actualAnnual: sumField(cashflowMonthlyBase.slice(10), 'actualMonthly'),
    actualCumulative: sumField(cashflowMonthlyBase, 'actualMonthly'),
  },
]
const constructionCashflowAnnual: CashflowAnnualLine[] = cashflowAnnual.map((row) => ({
  ...row,
  id: `cc-${row.id}`,
}))

const contingencySeries: ContingencyPoint[] = [
  { label: 'Aug-25', actualExpenditure: 4, forecastExpenditure: 8, actualContingency: 100, forecastContingency: 90 },
  { label: 'Sep-25', actualExpenditure: 8, forecastExpenditure: 12, actualContingency: 86, forecastContingency: 82 },
  { label: 'Oct-25', actualExpenditure: 12, forecastExpenditure: 18, actualContingency: 70, forecastContingency: 72 },
  { label: 'Nov-25', actualExpenditure: 18, forecastExpenditure: 26, actualContingency: 60, forecastContingency: 63 },
  { label: 'Dec-25', actualExpenditure: 28, forecastExpenditure: 34, actualContingency: 48, forecastContingency: 54 },
  { label: 'Jan-26', actualExpenditure: 36, forecastExpenditure: 42, actualContingency: 38, forecastContingency: 45 },
  { label: 'Feb-26', actualExpenditure: 44, forecastExpenditure: 50, actualContingency: 30, forecastContingency: 35 },
  { label: 'Mar-26', actualExpenditure: 52, forecastExpenditure: 58, actualContingency: 24, forecastContingency: 28 },
  { label: 'Apr-26', actualExpenditure: 60, forecastExpenditure: 66, actualContingency: 18, forecastContingency: 22 },
  { label: 'May-26', actualExpenditure: 68, forecastExpenditure: 74, actualContingency: 12, forecastContingency: 16 },
  { label: 'Jun-26', actualExpenditure: 76, forecastExpenditure: 82, actualContingency: 8, forecastContingency: 10 },
  { label: 'Jul-26', actualExpenditure: 84, forecastExpenditure: 90, actualContingency: 4, forecastContingency: 6 },
  { label: 'Aug-26', actualExpenditure: 92, forecastExpenditure: 95, actualContingency: 2, forecastContingency: 3 },
  { label: 'Sep-26', actualExpenditure: 100, forecastExpenditure: 100, actualContingency: 1, forecastContingency: 1 },
]

const contingencyTable: ContingencyTableRow[] = contingencySeries.map((point, idx) => ({
  id: `ct-${idx}`,
  dateLabel: point.label.toUpperCase(),
  percentage: point.actualContingency,
}))

export const mockPcgReport: PcgReport = {
  meta: mockPcgReportMeta,
  documentStatus: mockPcgReportMeta.documentStatus,
  projectStartDate: '2025-09-20',
  programCompletionDate: '2027-09-01',
  constructionStartDate: '2026-01-01',
  constructionPracticalCompletionDate: '2026-06-01',
  budget: budgetLines,
  variations: variationLines,
  provisionalSumRegister: provisionalSumRegisterLines,
  budgetTransfers,
  cashflowMonthly,
  cashflowAnnual,
  constructionCashflowMonthly,
  constructionCashflowAnnual,
  contingencySeries,
  contingencyTable,
  reportBuilderNotes: '',
  reportBuilderSections: buildDefaultReportBuilderSections(),
}

export const mockPcgReportStatuses: { value: PcgReportStatus; label: string; tone: PcgReportStatusTone }[] = [
  { value: 'draft', label: 'Draft', tone: 'muted' },
  { value: 'final', label: 'Final', tone: 'success' },
]
